/**
 * WhatsApp 24-hour session window helpers (#1471)
 *
 * The API computes the window (staff-api `computeSessionWindow`); these helpers
 * only decide how to present it and how to keep it current between fetches.
 */

import { ApiError } from '@/lib/api';
import type { Conversation, SessionWindow } from '@/types';

export const CLOSED_SESSION_WINDOW: SessionWindow = {
  active: false,
  lastInboundAt: null,
  expiresAt: null,
  minutesRemaining: null,
};

export const SESSION_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Whether the composer must switch to template mode: WhatsApp conversations
 * whose window the API reported as closed. A missing window (other channels, or
 * an API that predates this field) leaves free-form sending alone.
 */
export function requiresTemplate(conversation: Pick<Conversation, 'channel' | 'sessionWindow'>): boolean {
  if (conversation.channel !== 'whatsapp') return false;
  const window = conversation.sessionWindow;
  return window != null && !window.active;
}

/** Split the remaining minutes into whole hours + minutes for interpolation. */
export function splitRemaining(minutesRemaining: number): { hours: number; minutes: number } {
  const safe = Math.max(0, minutesRemaining);
  return { hours: Math.floor(safe / 60), minutes: safe % 60 };
}

/**
 * The window carried by a refused send. staff-api answers a free-form send into
 * a closed window with 409 + `code: 'SESSION_WINDOW_CLOSED'`; returning the
 * window lets the UI switch to template mode immediately instead of showing a
 * generic failure and leaving the composer in a mode that cannot work.
 */
export function sessionWindowFromError(error: unknown): SessionWindow | null {
  if (!(error instanceof ApiError)) return null;

  const data = error.data as { code?: string; sessionWindow?: SessionWindow } | null | undefined;
  if (data?.code !== 'SESSION_WINDOW_CLOSED') return null;

  return data.sessionWindow ?? CLOSED_SESSION_WINDOW;
}

/**
 * The window an inbound message opens. A customer reply reopens the full 24 h,
 * so the header can update from the WebSocket event instead of waiting for the
 * next conversation fetch.
 */
export function windowOpenedBy(inboundSentAt: string, now: Date = new Date()): SessionWindow {
  const lastInbound = new Date(inboundSentAt);
  const base = Number.isNaN(lastInbound.getTime()) ? now : lastInbound;
  const expiresAtMs = base.getTime() + SESSION_WINDOW_MS;
  const remainingMs = expiresAtMs - now.getTime();
  const active = remainingMs > 0;

  return {
    active,
    lastInboundAt: base.toISOString(),
    expiresAt: new Date(expiresAtMs).toISOString(),
    minutesRemaining: active ? Math.ceil(remainingMs / 60_000) : null,
  };
}
